import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  ContentDialog,
  ContentDialogInit,
} from "../../../../components/MDDForm/components/ContentDialog.jsx";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";
import {
  fetchClaimFormSectionTemplateAffectedForms,
  propagateClaimFormSectionTemplate,
} from "@accredo/fe-shared/services/claimFormSectionTemplateService.js";
import { diffComponents } from "../../../../utils/componentsDiff.js";

function DiffChips({ diff }) {
  if (diff.isUpToDate) {
    return <Chip size="small" label="Aktuell" />;
  }

  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {diff.added.length > 0 && (
        <Chip size="small" color="success" variant="outlined" label={`+${diff.added.length} neu`} />
      )}
      {diff.removed.length > 0 && (
        <Chip size="small" color="error" variant="outlined" label={`-${diff.removed.length} entfernt`} />
      )}
      {diff.changed.length > 0 && (
        <Chip size="small" color="warning" variant="outlined" label={`~${diff.changed.length} geändert`} />
      )}
    </Box>
  );
}

function fieldLabel(field, byName) {
  return byName.get(field)?.label || field;
}

function SectionTemplatePropagationContent({ sectionTemplate, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [isPropagating, setIsPropagating] = useState(false);
  const [affectedForms, setAffectedForms] = useState([]);
  const [selectedFormIds, setSelectedFormIds] = useState(new Set());

  useEffect(() => {
    setIsLoading(true);
    fetchClaimFormSectionTemplateAffectedForms(sectionTemplate.id)
      .then((res) => {
        const forms = res?.data ?? [];
        setAffectedForms(forms);
        setSelectedFormIds(
          new Set(
            forms
              .filter(
                (form) => !diffComponents(form.current_components, res.template_components).isUpToDate
              )
              .map((form) => form.id)
          )
        );
      })
      .catch(() =>
        enqueueSnackbar("Betroffene Formulare konnten nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionTemplate.id]);

  const rows = useMemo(
    () =>
      affectedForms.map((form) => ({
        form,
        diff: diffComponents(form.current_components, sectionTemplate.components),
      })),
    [affectedForms, sectionTemplate.components]
  );

  const toggleForm = (id) => {
    setSelectedFormIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handlePropagate = async () => {
    setIsPropagating(true);
    try {
      const res = await propagateClaimFormSectionTemplate(
        sectionTemplate.id,
        [...selectedFormIds]
      );
      enqueueSnackbar(`${res?.propagated_count ?? 0} Formular(e) aktualisiert`, {
        variant: "success",
      });
      onClose();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ?? "Propagierung fehlgeschlagen",
        { variant: "error" }
      );
    } finally {
      setIsPropagating(false);
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (!rows.length) {
    return (
      <Typography color="text.secondary">
        Kein Schadenformular verwendet dieses Section-Template.
      </Typography>
    );
  }

  return (
    <Box>
      <List disablePadding>
        {rows.map(({ form, diff }) => (
          <ListItem key={form.id} sx={{ px: 0, alignItems: "flex-start" }}>
            <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
              <Checkbox
                checked={selectedFormIds.has(form.id)}
                disabled={diff.isUpToDate}
                onChange={() => toggleForm(form.id)}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">{form.label}</Typography>
                  <DiffChips diff={diff} />
                </Box>
              }
              secondary={
                !diff.isUpToDate && (
                  <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                    {diff.added.length > 0 &&
                      `Neu: ${diff.added.map((f) => fieldLabel(f, diff.newByName)).join(", ")}. `}
                    {diff.removed.length > 0 &&
                      `Entfernt: ${diff.removed.map((f) => fieldLabel(f, diff.oldByName)).join(", ")}. `}
                    {diff.changed.length > 0 &&
                      `Geändert: ${diff.changed.map((f) => fieldLabel(f, diff.newByName)).join(", ")}.`}
                  </Box>
                )
              }
            />
          </ListItem>
        ))}
      </List>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="primary"
          disabled={selectedFormIds.size === 0 || isPropagating}
          onClick={handlePropagate}
        >
          Ausgewählte propagieren ({selectedFormIds.size})
        </Button>
      </Box>
    </Box>
  );
}

export default function SectionTemplatePropagationModal({ open, onClose, sectionTemplate }) {
  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }

    setOpenCD(true);
    setTca({
      title: `Betroffene Formulare: ${sectionTemplate?.name ?? ""}`,
      maxWidth: "sm",
      content: (
        <SectionTemplatePropagationContent sectionTemplate={sectionTemplate} onClose={onClose} />
      ),
      actions: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sectionTemplate?.id]);

  return <ContentDialog openCD={openCD} setOpenCD={setOpenCD} tca={tca} onClose={onClose} />;
}
